import { Component } from '@theme/component';

// Dynamically picks a legible badge text colour by sampling the brightness of
// the image area sitting behind each product-card badge. It re-evaluates on
// hover, when the gallery swaps to its secondary (lifestyle) image, so the
// label stays readable on whatever shot is currently showing.
//
// This is a standard theme web component (extends the shared `Component` base
// class, loaded as an ES module via scripts.liquid) to match the rest of the
// product-card system (`<product-card>`, `<slideshow-component>`, etc.), which
// is built on web components rather than Alpine.

const BRIGHTNESS_THRESHOLD = 145;

// Fraction of the image (from the top-left corner) that sits behind the badge.
const SAMPLE_REGION = { width: 0.4, height: 0.25 };

const brightnessCache = new Map();

/** Build a small variant of the image URL so the brightness probe stays cheap. */
function sampledSource(image) {
  const source = image.currentSrc || image.src;
  if (!source) return '';

  try {
    const url = new URL(source, window.location.href);
    url.searchParams.set('width', '240');
    return url.toString();
  } catch {
    return source;
  }
}

/** Average perceived brightness (0-255) of the badge region of an image. */
async function regionBrightness(source) {
  if (brightnessCache.has(source)) return brightnessCache.get(source);

  const probe = new Image();
  probe.crossOrigin = 'anonymous';
  probe.src = source;
  await probe.decode();

  const width = 48;
  const height = 30;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  context.drawImage(
    probe,
    0,
    0,
    probe.naturalWidth * SAMPLE_REGION.width,
    probe.naturalHeight * SAMPLE_REGION.height,
    0,
    0,
    width,
    height
  );

  const { data } = context.getImageData(0, 0, width, height);
  let total = 0;
  for (let i = 0; i < data.length; i += 4) {
    total += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }

  const brightness = total / (data.length / 4);
  brightnessCache.set(source, brightness);
  return brightness;
}

class ProductCardBadge extends Component {
  connectedCallback() {
    super.connectedCallback();

    const card = this.closest('.product-card');
    this.gallery = card?.querySelector('.card-gallery') ?? null;

    const images = this.gallery
      ? [...this.gallery.querySelectorAll('slideshow-slide:not([hidden]) .product-media__image')]
      : [];

    this.primaryImage = images[0] ?? null;
    this.hoverImage = images[1] ?? null;

    if (!this.primaryImage) return;

    this.applyColor(this.primaryImage);

    if (this.gallery && this.hoverImage) {
      this.handleEnter = () => this.applyColor(this.hoverImage);
      this.handleLeave = () => this.applyColor(this.primaryImage);
      this.gallery.addEventListener('pointerenter', this.handleEnter);
      this.gallery.addEventListener('pointerleave', this.handleLeave);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.gallery && this.handleEnter) {
      this.gallery.removeEventListener('pointerenter', this.handleEnter);
      this.gallery.removeEventListener('pointerleave', this.handleLeave);
    }
  }

  async applyColor(image) {
    const source = sampledSource(image);

    // Lazy images may not have resolved a source yet; retry once they load.
    if (!source) {
      image.addEventListener('load', () => this.applyColor(image), { once: true });
      return;
    }

    try {
      const brightness = await regionBrightness(source);
      this.classList.toggle('product-card-badge--light', brightness < BRIGHTNESS_THRESHOLD);
    } catch {
      // Cross-origin or decode failure: keep the default (dark) colour.
    }
  }
}

if (!customElements.get('product-card-badge')) {
  customElements.define('product-card-badge', ProductCardBadge);
}
